import { useState } from "react";
import axios from "axios";
import { Edit3, Plus, Save, X, Loader2, MapPin } from "lucide-react";
import { PinDropMap } from "@/components/PinDropMap";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Compact admin edit dialog. Directly PATCHes /api/admin/depots/{id}, so
 * changes skip the moderation queue. Handles the common "fix pin location /
 * name" case as well as core metadata (city, state, railroad, year_built).
 * Advanced/rarely-touched fields (museum_status, visiting_hours, etc.) stay
 * in the RailCam-style targeted dialogs.
 */
export function AdminEditDepotDialog({ depot, onChange, trigger, mode }) {
  const isCreate = mode === "create" || !depot;
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  const emptyForm = {
    name: "",
    city: "",
    state: "",
    state_code: "",
    latitude: "",
    longitude: "",
    status: "original",
    railroad: "",
    year_built: "",
    address: "",
    description: "",
    amtrak_station_code: "",
    on_active_rail_line: false,
    museum_status: "",
  };

  const openDialog = () => {
    if (isCreate) {
      setForm(emptyForm);
    } else {
      setForm({
        name: depot.name || "",
        city: depot.city || "",
        state: depot.state || "",
        state_code: depot.state_code || "",
        latitude: depot.latitude ?? "",
        longitude: depot.longitude ?? "",
        status: depot.status || "original",
        railroad: depot.railroad || "",
        year_built: depot.year_built ?? "",
        address: depot.address || "",
        description: depot.description || "",
        amtrak_station_code: depot.amtrak_station_code || "",
        on_active_rail_line: !!depot.on_active_rail_line,
        museum_status: depot.museum_status || "",
      });
    }
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (isCreate) {
      if (!form.city.trim() || !form.state_code.trim()) {
        toast.error("City and state code are required");
        return;
      }
    }
    const lat = form.latitude === "" ? null : parseFloat(form.latitude);
    const lng = form.longitude === "" ? null : parseFloat(form.longitude);
    if (isCreate && (lat == null || lng == null)) {
      toast.error("Latitude and longitude are required for new depots");
      return;
    }
    if (lat != null && (isNaN(lat) || lat < -90 || lat > 90)) {
      toast.error("Latitude must be between -90 and 90");
      return;
    }
    if (lng != null && (isNaN(lng) || lng < -180 || lng > 180)) {
      toast.error("Longitude must be between -180 and 180");
      return;
    }
    const yr = form.year_built === "" ? null : parseInt(form.year_built, 10);
    try {
      setSaving(true);
      const payload = {
        name: form.name.trim(),
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        state_code: (form.state_code || "").trim().toUpperCase() || null,
        latitude: lat,
        longitude: lng,
        status: form.status || "original",
        railroad: form.railroad.trim() || null,
        year_built: yr,
        address: form.address.trim() || null,
        description: form.description.trim() || null,
        amtrak_station_code: form.amtrak_station_code.trim().toUpperCase() || null,
        on_active_rail_line: !!form.on_active_rail_line,
        museum_status: form.museum_status || null,
      };
      let r;
      if (isCreate) {
        // For create, the POST /api/depots endpoint doesn't accept some fields
        // (museum_status, amtrak_station_code, has_railcam) — send only the
        // core create fields, then PATCH the rest immediately after.
        const {
          amtrak_station_code,
          on_active_rail_line,
          museum_status,
          ...core
        } = payload;
        r = await axios.post(`${API}/depots`, core, { withCredentials: true });
        const created = r.data;
        // Follow-up patch for the extended fields, if any were set.
        if (amtrak_station_code || on_active_rail_line || museum_status) {
          r = await axios.patch(
            `${API}/admin/depots/${created.id}`,
            {
              amtrak_station_code: amtrak_station_code,
              on_active_rail_line: on_active_rail_line,
              museum_status: museum_status,
            },
            { withCredentials: true }
          );
        }
        toast.success("Depot created");
      } else {
        r = await axios.patch(`${API}/admin/depots/${depot.id}`, payload, {
          withCredentials: true,
        });
        toast.success("Depot updated");
      }
      setOpen(false);
      onChange?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {trigger ? (
        <span onClick={openDialog}>{trigger}</span>
      ) : (
        <Button
          variant="outline"
          className={`w-full ${
            isCreate
              ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20 hover:!text-emerald-200 hover:border-emerald-500/60"
              : "bg-amber-500/10 border-amber-500/40 text-amber-300 hover:bg-amber-500/20 hover:!text-amber-200 hover:border-amber-500/60"
          }`}
          onClick={openDialog}
          data-testid={isCreate ? "admin-add-depot-btn" : "admin-edit-depot-btn"}
        >
          {isCreate ? <Plus className="w-4 h-4 mr-2" /> : <Edit3 className="w-4 h-4 mr-2" />}
          {isCreate ? "Add New Depot" : "Edit Depot (Admin)"}
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="sm:max-w-2xl bg-[#1a1a1a] border-white/10 max-h-[90vh] overflow-y-auto"
          data-testid="admin-edit-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2 font-serif">
              <Edit3 className="w-4 h-4 text-amber-500" />
              {isCreate ? "Add a new depot" : "Edit depot"}
            </DialogTitle>
            <DialogDescription className="text-white/60">
              {isCreate
                ? "Adds directly to the live map — no moderation queue. Latitude and longitude are required. Tip: right-click the target spot on Google Maps to copy coordinates."
                : "Changes save immediately — no moderation queue. Use this to fix a wrong pin or update location data on a demolished / relocated depot."}
            </DialogDescription>
            {!isCreate && depot?.updated_at && (
              <p className="text-[11px] text-white/50 font-mono pt-1">
                Last edited by <span className="text-amber-400">{depot.updated_by || "unknown"}</span> ·{" "}
                {new Date(depot.updated_at).toLocaleString(undefined, {
                  year: "numeric", month: "short", day: "numeric",
                  hour: "numeric", minute: "2-digit",
                })}
              </p>
            )}
          </DialogHeader>

          {form && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
              <div className="sm:col-span-2">
                <Label className="text-xs text-white/80">Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1.5 bg-white/5 border-white/10 text-white"
                  data-testid="edit-name"
                />
              </div>

              <div>
                <Label className="text-xs text-white/80">City</Label>
                <Input
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  className="mt-1.5 bg-white/5 border-white/10 text-white"
                  data-testid="edit-city"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-white/80">State</Label>
                  <Input
                    value={form.state}
                    onChange={(e) => setForm({ ...form, state: e.target.value })}
                    className="mt-1.5 bg-white/5 border-white/10 text-white"
                    data-testid="edit-state"
                  />
                </div>
                <div>
                  <Label className="text-xs text-white/80">Code</Label>
                  <Input
                    value={form.state_code}
                    onChange={(e) =>
                      setForm({ ...form, state_code: e.target.value.toUpperCase() })
                    }
                    maxLength={2}
                    className="mt-1.5 bg-white/5 border-white/10 text-white uppercase"
                    data-testid="edit-state-code"
                  />
                </div>
              </div>

              <div className="sm:col-span-2">
                <Label className="text-xs text-white/80 mb-1.5 block">
                  Drop a pin to set location
                </Label>
                <PinDropMap
                  latitude={form.latitude}
                  longitude={form.longitude}
                  onPick={(lat, lng) =>
                    setForm({
                      ...form,
                      latitude: lat.toFixed(6),
                      longitude: lng.toFixed(6),
                    })
                  }
                  height={240}
                />
              </div>

              <div>
                <Label className="text-xs text-white/80 flex items-center gap-1.5">
                  <MapPin className="w-3 h-3 text-amber-500" />
                  Latitude
                </Label>
                <Input
                  type="number"
                  step="0.000001"
                  value={form.latitude}
                  onChange={(e) => setForm({ ...form, latitude: e.target.value })}
                  className="mt-1.5 bg-white/5 border-white/10 text-white font-mono"
                  data-testid="edit-latitude"
                />
              </div>

              <div>
                <Label className="text-xs text-white/80 flex items-center gap-1.5">
                  <MapPin className="w-3 h-3 text-amber-500" />
                  Longitude
                </Label>
                <Input
                  type="number"
                  step="0.000001"
                  value={form.longitude}
                  onChange={(e) => setForm({ ...form, longitude: e.target.value })}
                  className="mt-1.5 bg-white/5 border-white/10 text-white font-mono"
                  data-testid="edit-longitude"
                />
              </div>

              <div>
                <Label className="text-xs text-white/80">Status</Label>
                <Select
                  value={form.status}
                  onValueChange={(v) => setForm({ ...form, status: v })}
                >
                  <SelectTrigger
                    className="mt-1.5 bg-white/5 border-white/10 text-white"
                    data-testid="edit-status"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-white/10 text-white">
                    <SelectItem value="original">Original</SelectItem>
                    <SelectItem value="relocated">Relocated</SelectItem>
                    <SelectItem value="demolished">Demolished</SelectItem>
                    <SelectItem value="unknown">Unknown</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs text-white/80">Year built</Label>
                <Input
                  type="number"
                  value={form.year_built}
                  onChange={(e) => setForm({ ...form, year_built: e.target.value })}
                  className="mt-1.5 bg-white/5 border-white/10 text-white font-mono"
                  data-testid="edit-year"
                />
              </div>

              <div className="sm:col-span-2">
                <Label className="text-xs text-white/80">Railroad</Label>
                <Input
                  value={form.railroad}
                  onChange={(e) => setForm({ ...form, railroad: e.target.value })}
                  className="mt-1.5 bg-white/5 border-white/10 text-white"
                  data-testid="edit-railroad"
                />
              </div>

              <div className="sm:col-span-2">
                <Label className="text-xs text-white/80">Address</Label>
                <Input
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className="mt-1.5 bg-white/5 border-white/10 text-white"
                  data-testid="edit-address"
                />
              </div>

              <div className="sm:col-span-2">
                <Label className="text-xs text-white/80">Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  className="mt-1.5 bg-white/5 border-white/10 text-white resize-none"
                  data-testid="edit-description"
                />
              </div>

              {/* Active-line / Amtrak / Museum block */}
              <div className="sm:col-span-2 pt-2 border-t border-white/10">
                <p className="text-[10px] uppercase tracking-[0.2em] text-amber-500 font-semibold mb-3">
                  Active service &amp; museum status
                </p>
              </div>

              <div className="sm:col-span-2">
                <Label className="text-xs text-white/80 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!form.on_active_rail_line}
                    onChange={(e) =>
                      setForm({ ...form, on_active_rail_line: e.target.checked })
                    }
                    className="accent-amber-500"
                    data-testid="edit-active-rail"
                  />
                  On an active rail line (trains still pass here)
                </Label>
              </div>

              <div>
                <Label className="text-xs text-white/80">
                  Amtrak station code
                </Label>
                <div className="flex gap-2 mt-1.5">
                  <Input
                    value={form.amtrak_station_code}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        amtrak_station_code: e.target.value.toUpperCase(),
                      })
                    }
                    maxLength={4}
                    placeholder="e.g. CHI, DER, LNS"
                    className="bg-white/5 border-white/10 text-white uppercase font-mono flex-1"
                    data-testid="edit-amtrak-code"
                  />
                  {form.amtrak_station_code && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() =>
                        setForm({ ...form, amtrak_station_code: "" })
                      }
                      className="border-red-500/40 text-red-300 hover:bg-red-500/10 hover:!text-red-200"
                      data-testid="edit-amtrak-clear"
                    >
                      Clear
                    </Button>
                  )}
                </div>
                <p className="text-[10px] text-white/50 mt-1">
                  Clear this if the depot is no longer an active station.
                </p>
              </div>

              <div>
                <Label className="text-xs text-white/80">Museum status</Label>
                <Select
                  value={form.museum_status || "none"}
                  onValueChange={(v) =>
                    setForm({ ...form, museum_status: v === "none" ? "" : v })
                  }
                >
                  <SelectTrigger
                    className="mt-1.5 bg-white/5 border-white/10 text-white"
                    data-testid="edit-museum-status"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-white/10 text-white">
                    <SelectItem value="none">— None —</SelectItem>
                    <SelectItem value="active_station">Active station</SelectItem>
                    <SelectItem value="museum">Museum</SelectItem>
                    <SelectItem value="restored_display">Restored / on display</SelectItem>
                    <SelectItem value="adaptive_reuse">Adaptive reuse</SelectItem>
                    <SelectItem value="abandoned">Abandoned</SelectItem>
                    <SelectItem value="demolished">Demolished</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              className="text-white/80 hover:bg-white/5 hover:!text-white"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-amber-500 hover:bg-amber-600 text-black"
              data-testid="edit-save-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
